
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, XCircle, Eye, Trash2, Printer, Building2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface RecentActivityProps {
  submissions: any[];
  printerSuggestions: any[];
  rejectedCount: number;
  loading: boolean;
  deletingRejected: boolean;
  onBulkDeleteRejected: () => void;
  onViewSubmission: (submission: any) => void;
  navigate: (path: string) => void;
}

const RecentActivity = ({
  submissions,
  printerSuggestions,
  rejectedCount,
  loading,
  deletingRejected,
  onBulkDeleteRejected,
  onViewSubmission,
  navigate
}: RecentActivityProps) => {
  const recentSubmissions = submissions.slice(0, 3);
  const recentPrinterSuggestions = printerSuggestions.slice(0, 2);
  const allRecentItems = [...recentSubmissions, ...recentPrinterSuggestions]
    .sort((a, b) => new Date(b.submitted_at || b.created_at).getTime() - new Date(a.submitted_at || a.created_at).getTime())
    .slice(0, 5);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest submissions and suggestions awaiting review</CardDescription>
        </div>
        {rejectedCount > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={deletingRejected}>
                <Trash2 className="h-4 w-4 mr-2" />
                {deletingRejected ? 'Deleting...' : 'Delete All Rejected'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete All Rejected Submissions</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all {rejectedCount} rejected submissions from the database. 
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onBulkDeleteRejected}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete All Rejected
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : allRecentItems.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {allRecentItems.map((item) => {
              const isSubmission = 'submitted_at' in item;
              const status = item.status;
              const timestamp = isSubmission ? item.submitted_at : item.created_at;
              const name = isSubmission ? item.content?.name : item.name;
              const type = isSubmission ? item.type : `${item.type} suggestion`;
              
              return (
                <div key={item.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {status === 'pending' && <Clock className="h-5 w-5 text-yellow-500" />}
                    {status === 'approved' && <CheckCircle className="h-5 w-5 text-green-500" />}
                    {status === 'rejected' && <XCircle className="h-5 w-5 text-red-500" />}
                    {!isSubmission && item.type === 'printer' && <Printer className="h-4 w-4 text-blue-500 ml-1" />}
                    {!isSubmission && item.type === 'manufacturer' && <Building2 className="h-4 w-4 text-purple-500 ml-1" />}
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        {name || 'Untitled'} 
                        <span className="text-muted-foreground ml-2 capitalize">({type})</span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className={
                      status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }>
                      {status}
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => isSubmission ? onViewSubmission(item) : navigate("/admin/submissions")}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivity;
